import React, { useCallback, useSyncExternalStore } from 'react';
import {
  getMessengerDraft,
  subscribeMessengerDraft,
} from '@/components/Messenger/cache/messengerDraftsStore';
import { formatMessengerComposerDraftSubtitle } from '@/components/Messenger/shared/messengerComposerDraftPreview';
import { useMessengerThreadById } from '@/hooks/useMessengerThreadById';
import { useMessengerSidebarUnreadViewportSubtract } from '@/hooks/useMessengerSidebarUnreadViewportSubtract';
import {
  highlightPlainText,
  SEARCH_HIGHLIGHT_CLASS_MESSENGER,
} from '@/utils/highlightSearchTerms';
import { getMessengerThreadListSubtitle } from '@/components/Messenger/shared/messengerThreadListPreview';

export interface ChatSidebarGroupRowProps {
  threadId: string;
  label: string;
  isActive: boolean;
  pinned: boolean;
  searchHighlightWords: readonly string[];
  currentUserId: string | null;
  onSelect: (contact: { type: 'group'; threadId: string; title: string }) => void;
  onOpenContextMenu: (e: React.MouseEvent<HTMLButtonElement>, threadId: string) => void;
}

export const ChatSidebarGroupRow = React.memo(function ChatSidebarGroupRow({
  threadId,
  label,
  isActive,
  pinned,
  searchHighlightWords,
  currentUserId,
  onSelect,
  onOpenContextMenu,
}: ChatSidebarGroupRowProps) {
  const thread = useMessengerThreadById(threadId);
  const viewportSubtract = useMessengerSidebarUnreadViewportSubtract();
  const draft = useSyncExternalStore(
    useCallback(
      (onChange) => subscribeMessengerDraft('thread', threadId, onChange),
      [threadId]
    ),
    () => getMessengerDraft('thread', threadId),
    () => undefined
  );
  const subtitle =
    formatMessengerComposerDraftSubtitle(draft) ??
    getMessengerThreadListSubtitle(thread, currentUserId);
  const rawUnread = thread?.unread_count ?? 0;
  const sub =
    viewportSubtract.threadId === threadId && viewportSubtract.count > 0
      ? viewportSubtract.count
      : 0;
  const unread = Math.max(0, rawUnread - sub);

  const hl =
    searchHighlightWords.length > 0
      ? (s: string) => highlightPlainText(s, searchHighlightWords, SEARCH_HIGHLIGHT_CLASS_MESSENGER)
      : null;

  const handleClick = useCallback(() => {
    onSelect({ type: 'group', threadId, title: label });
  }, [onSelect, threadId, label]);

  return (
    <button
      type="button"
      className={`chatSidebar__item ${isActive ? 'chatSidebar__item_active' : ''}`}
      onClick={handleClick}
      onContextMenu={(e) => onOpenContextMenu(e, threadId)}
    >
      <span className="chatSidebar__itemContent">
        <span className="chatSidebar__itemLabelRow">
          <span className="chatSidebar__itemLabel">{hl ? hl(label) : label}</span>
        </span>
        <span className="chatSidebar__itemSubtitle">{hl ? hl(subtitle) : subtitle}</span>
      </span>
      {unread > 0 ? (
        <span className="messengerUnreadBadge">{unread > 99 ? '99+' : String(unread)}</span>
      ) : null}
      {pinned ? (
        <i className="fa-solid fa-thumbtack chatSidebar__rowPinFa" aria-hidden title="Закреплено" />
      ) : null}
    </button>
  );
});
